import { useState, useEffect } from 'react';
import { GetApp } from '@mui/icons-material';
import { Box, IconButton, Tooltip } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

// Components do projeto
import KOGCollapse from '../kogCollapse/kogCollapse';
import KOGPaginationGrid from '../paginationGrid/paginationGrid';
import KOGImage from '../kogImage/kogImage';

// Utils e API
import { quoteApi } from '@/main/api';
import { getTranslateByKey } from '@/main/services/utils';
import { PaginationEnum } from '@/main/services/enum/PaginationEnum';
import { QuoteImageResponse } from '@/main/models/response';

// Styles
import * as S from './styles';

interface ICollapseImageCategoryProps {
  title: string;
  categoryType: 'initial' | 'supplementary';
  guidQuote: string;
  guidClaim?: string;
  expanded: boolean;
  onToggle: (expanded: boolean) => void;
  onPreview: (
    index: number,
    data: QuoteImageResponse[],
    totalRecords: number
  ) => void;
  canDownloadImages?: boolean;
  handleDownloadImage: (url: string, imageRecord?: QuoteImageResponse) => void;
  collapseMaxHeight?: string;
  collapseOverflow?: string;
  removeDeleteButton: boolean;
  handDeleteImage: (guid: string) => void;
  reloadTrigger?: number;
}

function CollapseImageCategory({
  title,
  categoryType,
  guidQuote,
  guidClaim,
  expanded,
  onToggle,
  onPreview,
  canDownloadImages,
  handleDownloadImage,
  collapseMaxHeight,
  collapseOverflow,
  removeDeleteButton,
  handDeleteImage,
  reloadTrigger
}: ICollapseImageCategoryProps) {
  const classes = S.useStyles();

  const [dataGrid, setDataGrid] = useState<QuoteImageResponse[]>([]);
  const [totalRecords, setTotalRecords] = useState<number>(0);
  const [formFilters, setFormFilters] = useState<any | null>(null);

  const ID_SCROLLABLE_TARGET = `id-scrollable-${categoryType}`;

  useEffect(() => {
    setDataGrid([]);
    setTotalRecords(0);
    setFormFilters({
      guidClaim: guidClaim ?? null,
      guidQuote: guidQuote,
      pageIndex: PaginationEnum.PageIndexIntial,
      pageSize: PaginationEnum.PageSize10,
      includeInitialQuoteImages: categoryType === 'initial',
      includeComplementQuoteImages: categoryType === 'supplementary',
      _reloadTrigger: reloadTrigger
    });
  }, [guidQuote, guidClaim, categoryType, reloadTrigger]);

  useEffect(() => {
    if (!expanded) {
      setDataGrid([]);
      setTotalRecords(0);
    }
  }, [expanded]);

  function fetchData(request: any) {
    return quoteApi.getAllClaimImages(request).then(response => response.data);
  }

  return (
    <KOGCollapse
      header={<span style={{ fontWeight: 'bold' }}>{title}</span>}
      isOpenCollapseinitial={expanded}
      forceCollapseTrigger={!expanded}
      onCustomCollapse={onToggle}
      maxHeight={collapseMaxHeight}
      overflow={collapseOverflow}
      isFlexAccordion
      content={
        <Box
          px={1}
          py={1.5}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            flex: 1,
            minHeight: 0
          }}>
          <S.PaginationLabel>
            {totalRecords > 0 &&
              getTranslateByKey('information.photoPaginationCount')
                .replace('{0}', dataGrid.length.toString())
                .replace('{1}', totalRecords.toString())}
          </S.PaginationLabel>
          <S.ContainerScoll
            id={ID_SCROLLABLE_TARGET}
            style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
            <KOGPaginationGrid
              idScrollableTarget={ID_SCROLLABLE_TARGET}
              showTitleResults={false}
              showEndMessage={false}
              totalRecords={totalRecords}
              setTotalRecords={setTotalRecords}
              dataGrid={dataGrid}
              formFilters={formFilters}
              setDataGrid={setDataGrid}
              fetchMore={fetchData}>
              {dataGrid.length > 0 && (
                <S.ContainerPreview previewImage={'column'}>
                  {dataGrid.map((quoteImage, index) => (
                    <S.BoxImage
                      key={`${quoteImage.guid}-${index}`}
                      id={`id-${categoryType}-itemPreview`}
                      style={{ position: 'relative' }}>
                      <S.ItemPreview>
                        <KOGImage
                          src={quoteImage.externalKey}
                          id={`id-${categoryType}-img-preview-${quoteImage.fileName}`}
                          onClick={() =>
                            onPreview(index, dataGrid, totalRecords)
                          }
                        />
                        {!removeDeleteButton && (
                          <S.ButtonDeletePreview
                            id={`id-dragDrop-btn-removePreview-${index}`}
                            onClick={() => handDeleteImage(quoteImage.guid)}>
                            <CloseIcon fontSize="small" />
                          </S.ButtonDeletePreview>
                        )}
                      </S.ItemPreview>
                      <Box
                        display="flex"
                        width="100%"
                        justifyContent="space-between"
                        alignItems="center">
                        <S.LabelFileName>{quoteImage.fileName}</S.LabelFileName>

                        {canDownloadImages && (
                          <Tooltip
                            title={getTranslateByKey(
                              'information.downloadFile'
                            )}>
                            <IconButton
                              className={classes.buttonBaseDeletePreview}
                              id={`id-${categoryType}-btn-download-${index}`}
                              onClick={e => {
                                e.preventDefault();
                                e.stopPropagation();
                                handleDownloadImage(quoteImage.externalKey, quoteImage);
                              }}>
                              <GetApp fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>

                      {/* {canDownloadImages && (
                        <Tooltip
                          title={getTranslateByKey('information.downloadFile')}>
                          <div
                            id={`id-${categoryType}-btn-download-${index}`}
                            style={{
                              position: 'absolute',
                              bottom: '8px',
                              right: '8px',
                              backgroundColor: '#fff',
                              borderRadius: '50%',
                              width: '28px',
                              height: '28px',
                              display: 'flex',
                              justifyContent: 'center',
                              alignItems: 'center',
                              cursor: 'pointer',
                              color: '#8e24aa',
                              boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                            }}
                            onClick={e => {
                              e.preventDefault();
                              e.stopPropagation();
                              handleDownloadImage(quoteImage.externalKey);
                            }}>
                            <GetApp fontSize="small" />
                          </div>
                        </Tooltip>
                      )} */}
                    </S.BoxImage>
                  ))}
                </S.ContainerPreview>
              )}
            </KOGPaginationGrid>
          </S.ContainerScoll>
        </Box>
      }
    />
  );
}

export default CollapseImageCategory;
